import { el } from "./dom.js";

const ENGAGEMENT_LABELS = {
	want_to_check: "On my radar",
	haven_started: "Planning to start",
	poked: "Poked at it",
	playing: "Playing",
	on_pause: "Stopped (for now)",
	done: "Done",
	replaying: "Replaying",
	bounced: "Bounced",
	// A deliberate "no" — aware of it, decided not to engage. Terminal, so it sits
	// at the end of the list. Media-specific maps below fall back to this label.
	not_interested: "Not interested",
};

const OWNERSHIP_LABELS = {
	want_it: "Want it",
	have_it: "Have it",
	had_it: "Had it",
	friend_has_it: "Friend's got it",
	// Books-only: you have a copy on loan (library / borrowed) rather than owning it.
	borrowed: "Library / borrowed",
	// The neutral negative: you don't own it and aren't wishlisting it (e.g. a show
	// you stream, a game you played at a friend's but won't buy). Distinct from
	// want_it / had_it / friend_has_it.
	not_owned: "Don't own it",
};

// Orthogonal to ownership status: *how* you have/had it. Multi-select — a game
// can be both physical and digital. Only meaningful for have_it / had_it.
const FORMAT_LABELS = {
	physical: "Physical",
	digital: "Digital",
	subscription: "Subscription / streaming",
};

// Book formats (multi-select, same role as game FORMAT_LABELS).
const BOOK_FORMAT_LABELS = {
	hardcover: "Hardcover",
	paperback: "Paperback",
	ebook: "E-book",
	audiobook: "Audiobook",
	// e.g. Kindle Unlimited / Audible / a library app — access via subscription.
	subscription: "Subscription",
};

// Engagement labels are media-aware: the values are shared across media types
// but a book reads "Reading"/"Read", not "Playing"/"Done". Anything not
// overridden falls back to the generic (game) label.
const ENGAGEMENT_LABELS_BOOK = {
	want_to_check: "On my radar",
	haven_started: "To read",
	poked: "Started",
	playing: "Reading",
	on_pause: "Paused",
	done: "Read",
	replaying: "Re-reading",
	bounced: "Did not finish",
};

// TV uses watch-oriented wording.
const ENGAGEMENT_LABELS_TV = {
	want_to_check: "Want to watch",
	haven_started: "To watch",
	poked: "Started",
	playing: "Watching",
	on_pause: "Paused",
	done: "Watched",
	replaying: "Rewatching",
	bounced: "Dropped",
};

// Anime is watched media too — same wording as TV.
const ENGAGEMENT_LABELS_ANIME = {
	want_to_check: "Want to watch",
	haven_started: "To watch",
	poked: "Started",
	playing: "Watching",
	on_pause: "Paused",
	done: "Watched",
	replaying: "Rewatching",
	bounced: "Dropped",
};

// Movies are watched media too — same wording as TV.
const ENGAGEMENT_LABELS_MOVIE = {
	want_to_check: "Want to watch",
	haven_started: "To watch",
	poked: "Started",
	playing: "Watching",
	on_pause: "Paused",
	done: "Watched",
	replaying: "Rewatching",
	bounced: "Dropped",
};

// Comics are read — same wording as books.
const ENGAGEMENT_LABELS_COMIC = ENGAGEMENT_LABELS_BOOK;

const ENGAGEMENT_OVERRIDES_BY_MEDIA = {
	book: ENGAGEMENT_LABELS_BOOK,
	tv: ENGAGEMENT_LABELS_TV,
	anime: ENGAGEMENT_LABELS_ANIME,
	movie: ENGAGEMENT_LABELS_MOVIE,
	comic: ENGAGEMENT_LABELS_COMIC,
};

const ALL_LABELS = {
	...ENGAGEMENT_LABELS,
	...OWNERSHIP_LABELS,
	...FORMAT_LABELS,
	...BOOK_FORMAT_LABELS,
};

export const ENGAGEMENT_STATUSES = Object.keys(ENGAGEMENT_LABELS);
// Ownership statuses are listed explicitly (not derived from the label map) so a
// books-only status like `borrowed` doesn't leak into the games picker.
export const OWNERSHIP_STATUSES = [
	"want_it",
	"have_it",
	"had_it",
	"friend_has_it",
	"not_owned",
];
export const BOOK_OWNERSHIP_STATUSES = [
	"want_it",
	"have_it",
	"had_it",
	"friend_has_it",
	"borrowed",
	"not_owned",
];
export const OWNERSHIP_FORMATS = Object.keys(FORMAT_LABELS);
export const BOOK_FORMATS = Object.keys(BOOK_FORMAT_LABELS);

// Engagement states that count as "you've actually played it" — the pool that's
// eligible for VS Battle ranking. Excludes want_to_check and haven_started.
export const PLAYED_STATUSES = [
	"poked",
	"playing",
	"on_pause",
	"done",
	"replaying",
	"bounced",
];

// How many times you've finished it — manual, single-select, only shown when
// engagement is "done". Non-overlapping buckets.
export const TIMES_FINISHED = [
	{ value: "1", label: "1" },
	{ value: "2-10", label: "2–10" },
	{ value: "11-20", label: "11–20" },
	{ value: "21-50", label: "21–50" },
	{ value: "51+", label: "51+" },
];

// Personal awards — multi-select *labels* applied alongside the (honest) star
// rating. Not superlatives: a label can apply to many games, so no "best ___"
// awards (those imply a single winner). A 2★ game can still be a Nostalgia pick.
export const AWARDS = [
	{ value: "comfort_pick", label: "Comfort pick" },
	{ value: "nostalgia_pick", label: "Nostalgia pick" },
	{ value: "guilty_pleasure", label: "Guilty pleasure" },
	{ value: "hall_of_fame", label: "Hall of Fame" },
	{ value: "hidden_gem", label: "Hidden gem" },
	{ value: "formative", label: "Formative" },
	{ value: "time_sink", label: "Time sink" },
	{ value: "tearjerker", label: "Tearjerker" },
	// Genre-defining / timeless — may have started a genre and been overshadowed
	// by successors, but still commands respect.
	{ value: "classic", label: "Classic" },
	// Thrives in company — party, couch, or online social/multiplayer (Guitar
	// Hero, Tony Hawk, fighting games, Among Us, Fall Guys…).
	{ value: "party_pick", label: "Party pick" },
	// Visual splendor — stunning art direction / how it looks. Fits any visual
	// medium (games, comics, illustrated/art books); rarely a plain-text novel.
	{ value: "feast_for_the_eyes", label: "Feast for the eyes" },
	// An artistic achievement in its own right — beautiful as a crafted whole.
	// The broader, most cross-media of the two.
	{ value: "work_of_art", label: "Work of art" },
	// Not exceptional in any one category — just a genuinely fun, good time.
	{ value: "a_blast", label: "A blast" },
];

const AWARD_BY_VALUE = Object.fromEntries(AWARDS.map((a) => [a.value, a]));

export function awardLabel(value) {
	return AWARD_BY_VALUE[value]?.label ?? value;
}

// Optional mediaType picks media-specific wording (e.g. "Reading" for books).
export function statusLabel(status, mediaType) {
	const overrides = mediaType
		? ENGAGEMENT_OVERRIDES_BY_MEDIA[mediaType]
		: null;
	return overrides?.[status] ?? ALL_LABELS[status] ?? status;
}

export function statusBadge(status, mediaType) {
	if (!status) return el("span", {}, "");
	const label = statusLabel(status, mediaType);
	return el(`span.badge.badge--${status}`, { title: label }, label);
}
